import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Card,
  Space,
  Upload,
  Alert,
  Result,
  Spin
} from 'antd';
import {
  ArrowLeftOutlined,
  UploadOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import Layout from '../../components/Layout';
import { useUser } from '../../contexts/userContext';
import { resellerService } from '../../services/reseller.service';
import { Reseller, ImportCouponsResponse } from '../../types/reseller.types';
import { extractErrorFromResponse, extractErrorMessage } from '../../helpers/errorHandler';
import '../../Styles/Resellers.scss';

const { Dragger } = Upload;

interface FormValues {
  resellerId: number;
  couponSetName: string;
  pricePerLicense: number;
  skipHeader: boolean;
}

const ImportCouponsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedResellerId = searchParams.get('resellerId');

  const { state: { isAdmin } } = useUser();
  const [form] = Form.useForm<FormValues>();
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importResult, setImportResult] = useState<ImportCouponsResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin === false) {
      navigate('/');
      return;
    }
    if (isAdmin) {
      loadResellers();
    }
  }, [isAdmin, navigate]);

  const loadResellers = async () => {
    try {
      const response = await resellerService.getResellers();
      if (response.isSuccess) {
        const activeResellers = (response.responseObject || []).filter((r) => r.isActive);
        setResellers(activeResellers);
        
        if (preselectedResellerId) {
          form.setFieldsValue({ resellerId: parseInt(preselectedResellerId) });
        }
      } else {
        const errMsg = extractErrorFromResponse(response, 'Failed to load resellers');
        setErrorMessage(errMsg);
      }
    } catch (error: any) {
      const errMsg = extractErrorMessage(error, 'Failed to load resellers. Please try again.');
      setErrorMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (fileList.length === 0) {
      setErrorMessage('Please select a CSV file');
      return;
    }

    const file = fileList[0].originFileObj as File;
    if (!file) {
      setErrorMessage('Invalid file');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    
    try {
      const response = await resellerService.importCoupons(
        values.resellerId,
        values.couponSetName,
        values.pricePerLicense,
        file,
        values.skipHeader
      );

      if (response.isSuccess) {
        setImportResult(response.responseObject);
      } else {
        const errMsg = extractErrorFromResponse(response, 'Import failed');
        setErrorMessage(errMsg);
      }
    } catch (error: any) {
      const errMsg = extractErrorMessage(error, 'Import failed. Please try again.');
      setErrorMessage(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = useCallback(({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    const latestFile = newFileList.slice(-1);
    setFileList(latestFile);
  }, []);

  const handleReset = () => {
    setImportResult(null);
    setFileList([]);
    form.resetFields();
    if (preselectedResellerId) {
      form.setFieldsValue({ resellerId: parseInt(preselectedResellerId) });
    }
  };

  if (isAdmin === null || loading) {
    return (
      <Layout>
        <div className="import-coupons-page">
          <div className="page-loading">
            <Spin 
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              tip="Loading..."
            />
          </div>
        </div>
      </Layout>
    );
  }

  if (importResult) {
    return (
      <Layout>
        <div className="import-coupons-page">
          <Card className="import-result-card">
            <Result
              status={importResult.failed > 0 ? 'warning' : 'success'}
              icon={<CheckCircleOutlined />}
              title="Import Completed"
              subTitle={importResult.message}
              extra={[
                <Button
                  type="primary"
                  key="another"
                  onClick={handleReset}
                >
                  Import Another
                </Button>,
                <Button
                  key="back"
                  onClick={() => navigate('/admin/resellers')}
                >
                  Back to Resellers
                </Button>
              ]}
            />

            <div className="import-stats">
              <div className="import-stat">
                <span className="import-stat__label">Total Processed</span>
                <span className="import-stat__value">{importResult.totalProcessed}</span>
              </div>
              <div className="import-stat import-stat--success">
                <span className="import-stat__label">Successfully Imported</span>
                <span className="import-stat__value">{importResult.successfullyImported}</span>
              </div>
              <div className="import-stat import-stat--warning">
                <span className="import-stat__label">Skipped (Duplicates)</span>
                <span className="import-stat__value">{importResult.skipped}</span>
              </div>
              <div className="import-stat import-stat--error">
                <span className="import-stat__label">Failed</span>
                <span className="import-stat__value">{importResult.failed}</span>
              </div>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="import-coupons-page">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/resellers')}
          className="back-button"
        >
          Back
        </Button>

        <h1>Import Coupon Codes</h1>

        <Card className="import-form-card">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ skipHeader: true, pricePerLicense: 9.99 }}
          >
            <Form.Item
              name="resellerId"
              label="Select Reseller"
              rules={[{ required: true, message: 'Please select a reseller' }]}
            >
              <Select
                placeholder="Select a reseller"
                size="large"
                options={resellers.map((r) => ({
                  value: r.id,
                  label: r.name
                }))}
              />
            </Form.Item>

            <Form.Item
              name="couponSetName"
              label="Coupon Set Name"
              rules={[
                { required: true, message: 'Please enter a name for this coupon set' },
                { max: 100, message: 'Name cannot exceed 100 characters' }
              ]}
            >
              <Input placeholder="BH_March_2026" size="large" />
            </Form.Item>

            <Form.Item
              name="pricePerLicense"
              label="Price Per License"
              rules={[
                { required: true, message: 'Please enter the price per license' },
                { type: 'number', min: 0, message: 'Price must be positive' }
              ]}
            >
              <InputNumber
                prefix="$"
                placeholder="9.99"
                size="large"
                precision={2}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              label="CSV File"
              required
              extra={
                <span className="form-hint">
                  Format: Coupon Code, Coupon Id, Coupon Set Name, Coupon Code Status
                </span>
              }
            >
              <Dragger
                accept=".csv"
                fileList={fileList}
                onChange={handleFileChange}
                beforeUpload={() => false}
                maxCount={1}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag CSV file here to upload
                </p>
                <p className="ant-upload-hint">
                  Only .csv files are supported
                </p>
              </Dragger>
            </Form.Item>

            <Alert
              message="CSV Format"
              description={
                <div>
                  <p>The CSV file should have the following columns:</p>
                  <ol>
                    <li><strong>Coupon Code</strong> - The activation code</li>
                    <li><strong>Coupon Id</strong> - External ID from Chargebee</li>
                    <li><strong>Coupon Set Name</strong> - Original set name</li>
                    <li><strong>Coupon Code Status</strong> - Status</li>
                  </ol>
                </div>
              }
              type="info"
              showIcon
              className="csv-format-alert"
            />

            <Form.Item className="form-actions">
              <Space>
                <Button onClick={() => navigate('/admin/resellers')}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  icon={<UploadOutlined />}
                  disabled={fileList.length === 0}
                >
                  Import
                </Button>
              </Space>
            </Form.Item>
          </Form>
          {errorMessage && (
          <Alert
            message="Error"
            description={errorMessage}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMessage(null)}
            style={{ marginBottom: 16, marginTop: 16 }}
          />
        )}
        </Card>
      </div>
    </Layout>
  );
};

export default ImportCouponsPage;
